// An emailed survey link's token is its only credential, so it stays in the
// URL and out of anything we report
const SURVEY_TOKEN = /\/workshop-survey\/[^/?#\s"']+/g;

/** Replaces a survey link's token in a path or URL with a placeholder */
export function redactSurveyToken(value: string): string {
  return value.replace(SURVEY_TOKEN, '/workshop-survey/:token');
}

/** An object literal: reported data, not a DOM node, error or class instance */
function isPlainObject(value: object): value is Record<string, unknown> {
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/** Redacts the token from every string in a reported value, at any depth */
export function redactSurveyTokens<T>(value: T): T {
  return redact(value, new WeakMap()) as T;
}

/** Redacts one value, resolving a container already seen to its result */
function redact(value: unknown, done: WeakMap<object, unknown>): unknown {
  if (typeof value === 'string') return redactSurveyToken(value);
  if (!value || typeof value !== 'object') return value;
  if (done.has(value)) return done.get(value);
  if (Array.isArray(value)) {
    const out = Object.isFrozen(value) ? [...value] : value;
    done.set(value, out);
    for (let i = 0; i < out.length; i++) {
      const next = redact(out[i], done);
      if (next !== out[i]) out[i] = next;
    }
    return out;
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = Object.isFrozen(value)
      ? Object.assign(Object.create(Object.getPrototypeOf(value)), value)
      : value;
    done.set(value, out);
    for (const key of Object.keys(out)) {
      const next = redact(out[key], done);
      if (next !== out[key]) out[key] = next;
    }
    return out;
  }
  return value;
}
